/**
 * AttendanceLeaderboard Component
 * 
 * Displays a ranked list of players by training attendance.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Trophy, Medal, User } from 'lucide-react';
import { ATTENDANCE_OPTIONS } from './constants';

/**
 * @param {Object} props
 * @param {Object[]} props.leaderboard - Array of player attendance data
 * @param {string|null} props.expandedPlayerId - Currently expanded player ID
 * @param {string|null} props.previousExpandedId - Previously expanded player ID (for animation)
 * @param {Function} props.onPlayerClick - Handler for player click (toggle expand)
 * @param {string} props.buttonColor - Theme button color
 */
export function AttendanceLeaderboard({
  leaderboard = [],
  expandedPlayerId = null,
  previousExpandedId = null,
  onPlayerClick,
  buttonColor = '#064E3B'
}) {
  // Calculate max sessions for progress bar width
  const maxSessions = Math.max(...leaderboard.map(p => p.total_sessions || 0), 1);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-4 h-4 text-amber-500" />;
    if (rank === 2) return <Medal className="w-4 h-4 text-gray-400" />;
    if (rank === 3) return <Medal className="w-4 h-4 text-amber-700" />;
    return <span className="text-sm font-medium text-gray-500">{rank}</span>;
  };

  if (leaderboard.length === 0) {
    return (
      <Card className="h-full" data-testid="attendance-leaderboard">
        <CardHeader>
          <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Attendance Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-400">
            <User className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No attendance data yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full" data-testid="attendance-leaderboard">
      <CardHeader>
        <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          Attendance Leaderboard
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {leaderboard.map((player) => {
            const isExpanded = expandedPlayerId === player.player_id;
            const wasExpanded = previousExpandedId === player.player_id;
            const attendanceRate = player.total_sessions > 0 
              ? ((player.attended / player.total_sessions) * 100).toFixed(0) 
              : 0;

            return (
              <div
                key={player.player_id}
                className={`rounded-xl transition-all duration-300 cursor-pointer ${
                  isExpanded ? 'bg-gray-100 shadow-sm' : 'hover:bg-gray-50'
                }`}
                onClick={() => onPlayerClick?.(player.player_id)}
                data-testid={`leaderboard-player-${player.player_id}`}
              >
                {/* Main Row */}
                <div className="flex items-center gap-3 p-3">
                  {/* Rank */}
                  <div className="w-7 h-7 flex items-center justify-center">
                    {getRankIcon(player.rank)}
                  </div>

                  {/* Photo/Number */}
                  {player.photo ? (
                    <img 
                      src={player.photo} 
                      alt={player.name} 
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: buttonColor }}
                    >
                      {player.jersey_number || player.name?.charAt(0) || '?'}
                    </div>
                  )}

                  {/* Name and Progress */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-gray-900 truncate">{player.name}</p>
                      <span className="text-sm font-semibold text-emerald-600 ml-2">
                        {player.attended}/{player.total_sessions}
                      </span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-1.5 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${(player.attended / maxSessions) * 100}%`,
                          backgroundColor: buttonColor
                        }}
                      />
                    </div>
                    
                    {/* Attendance Rate */}
                    <p className="text-xs text-gray-500 mt-1">{attendanceRate}% attendance rate</p>
                  </div>
                </div>

                {/* Expanded Details */}
                <div 
                  className={`overflow-hidden transition-all duration-300 ${
                    isExpanded ? 'max-h-40 opacity-100' : wasExpanded ? 'max-h-0 opacity-0' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-3 pb-3 pt-1">
                    <div className="p-3 bg-white rounded-lg border border-gray-200">
                      <p className="text-xs font-medium text-gray-500 mb-2">Status Breakdown</p>
                      <div className="flex flex-wrap gap-1.5">
                        {player.status_breakdown && Object.entries(player.status_breakdown).map(([status, count]) => {
                          if (count === 0) return null;
                          const option = ATTENDANCE_OPTIONS.find(o => o.value === status);
                          return (
                            <span 
                              key={status}
                              className={`text-xs px-2 py-0.5 rounded-full ${option?.color || 'bg-gray-100 text-gray-700'}`}
                            >
                              {option?.label || status}: {count}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default AttendanceLeaderboard;
